import Email from "@/domain/entities/emails/Email";
import Thread from "@/domain/entities/emails/Thread";
import { eventKeys } from "@/lib/eventKeys";
import { queryKeys } from "@/lib/queryKeys";
import { emailMapper } from "@/mappers/email/emailMapper";
import threadMapper from "@/mappers/email/threadMapper";
import { Mail, useSdk } from "@cs_teamwaydev/mailvista-sdk_react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import useGetEventBus from "./useGetEventBus";
import { useToast } from "./utils/useToast";

const useGetMailThread = (mail?: Email) => {
    const { mailManager } = useSdk();
    const queryClient = useQueryClient();
    const { toast } = useToast();
    const { t } = useTranslation();

    const fetchMailThread = async () => {
        if (!mail) {
            return new Thread({
                mails: [],
            });
        }

        try {
            // Argument copie (uniqueMailvistaId) : le SDK en dérive l'internetMessageId pour le thread
            // message-level. La queryKey reste en internetMessageId (sémantique conversation, cf. WS-F4).
            const thread = await mailManager.getMailThread(
                mail.uniqueMailvistaId,
            );

            return threadMapper(thread);
        } catch (error) {
            console.error(error);
            toast({
                title: t("errors.mailThread"),
                description: t("errors.global.description"),
                variant: "error",
            });
        }
    };

    const mailsThread = useQuery({
        queryKey: queryKeys.mailThread.detail(mail?.internetMessageId),
        queryFn: fetchMailThread,
        staleTime: 0,
        enabled: !!mail?.internetMessageId,
    });

    const handleThreadUpdated = (newMail: Mail) => {
        if (!mail || !mailsThread.data) {
            return;
        }

        if (!mailsThread.data) {
            return;
        }

        const newMailMapped = emailMapper(newMail);

        const existingMailIndex = mailsThread?.data?.mails.findIndex(
            (mailFromThread) =>
                mailFromThread.internetMessageId ===
                newMailMapped.internetMessageId,
        );

        if (
            existingMailIndex === -1 &&
            mail.conversationId === newMailMapped.conversationId
        ) {
            // mail sent
            const newMailsThread = new Thread({
                ...mailsThread.data,
                mails: [...mailsThread.data.mails, newMailMapped],
            });

            queryClient.setQueryData(
                queryKeys.mailThread.detail(mail.internetMessageId),
                newMailsThread,
            );

            toast({
                title: t("email.newMailSent.title"),
                description: t("email.newMailSent.description"),
                variant: "success",
            });
        }
    };

    const { off: offThreadUpdated } = useGetEventBus<Mail>(
        eventKeys.mails.threadUpdated, // used to add a new sender mail to the thread
        handleThreadUpdated,
    );

    // mail-updated is also handled in useGetMailsList (list cache) and useVistassistant (assistant store).
    // Each hook updates its own data layer independently — no shared handler is used intentionally.
    //
    // Enrichment mail-updated events can fire before the thread query resolves (mitt does not buffer): stash
    // them and replay once the data is available.
    const pendingMailUpdatesRef = useRef<Mail[]>([]);

    useEffect(() => {
        pendingMailUpdatesRef.current = [];
    }, [mail?.internetMessageId]);

    // Match by internetMessageId (message level), not uniqueMailvistaId: a thread shows one item per logical
    // message. See Doc/identite-mail/14 WS-F4 + 13 §3.7.
    const applyMailUpdatedToThread = (data: Thread, newMail: Mail): Thread => {
        const newMailMapped = emailMapper(newMail);

        const existingMailIndex = data.mails.findIndex(
            (mailFromThread) =>
                mailFromThread.internetMessageId ===
                newMailMapped.internetMessageId,
        );

        if (existingMailIndex !== -1) {
            // mail updated
            return new Thread({
                ...data,
                mails: data.mails.map((mailFromThread) =>
                    mailFromThread.internetMessageId ===
                    newMailMapped.internetMessageId
                        ? newMailMapped
                        : mailFromThread,
                ),
            });
        }

        if (mail?.conversationId === newMailMapped.conversationId) {
            // mail received
            return new Thread({
                ...data,
                mails: [...data.mails, newMailMapped],
            });
        }

        return data;
    };

    const handleMailUpdated = (newMail: Mail) => {
        if (!mail) {
            return;
        }

        const queryKey = queryKeys.mailThread.detail(mail.internetMessageId);

        if (!queryClient.getQueryData<Thread>(queryKey)) {
            pendingMailUpdatesRef.current.push(newMail);

            return;
        }

        // Functional updater (reads the current cache, not the closure): two close mail-updated events stack
        // instead of overwriting each other.
        queryClient.setQueryData<Thread>(queryKey, (old) =>
            old ? applyMailUpdatedToThread(old, newMail) : old,
        );
    };

    // Replay the mail-updated events that arrived before the thread data was available.
    useEffect(() => {
        if (!mail || pendingMailUpdatesRef.current.length === 0) {
            return;
        }

        const queryKey = queryKeys.mailThread.detail(mail.internetMessageId);
        if (!queryClient.getQueryData<Thread>(queryKey)) {
            return;
        }

        const pending = pendingMailUpdatesRef.current;
        pendingMailUpdatesRef.current = [];

        queryClient.setQueryData<Thread>(queryKey, (old) => {
            if (!old) {
                return old;
            }
            let data = old;
            pending.forEach((newMail) => {
                data = applyMailUpdatedToThread(data, newMail);
            });

            return data;
        });
    }, [mailsThread.data, mail?.internetMessageId]);

    const { off: offMailUpdated } = useGetEventBus<Mail>(
        eventKeys.mails.updated, // used to update a mail in the thread (for example with added attachments) or to add a new mail to the thread (receiver)
        handleMailUpdated,
    );

    useEffect(
        () => () => {
            offThreadUpdated(
                eventKeys.mails.threadUpdated,
                handleThreadUpdated,
            );
            offMailUpdated(eventKeys.mails.updated, handleMailUpdated);
        },
        [],
    );

    return mailsThread;
};

export default useGetMailThread;
